import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSend, FiMessageSquare, FiUser, FiClock, FiCheck, FiCheckCircle, FiBriefcase, FiShield } = FiIcons;

const InternalChat = ({ currentUserType }) => {
  const { user } = useAuth();
  
  // SIKKERHET: Default til tomme arrays og no-op funksjoner
  const { 
    sendInternalMessage = () => {}, 
    getChatHistory = () => [], 
    markMessagesAsRead = () => {}, 
    partners = [], 
    sellers = [], 
    internalMessages = [] 
  } = useData();

  const [activeConversation, setActiveConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);

  // SIKKERHET: Sjekk at lister faktisk er arrays før vi kjører .map()
  const safePartners = Array.isArray(partners) ? partners : [];
  const safeSellers = Array.isArray(sellers) ? sellers : [];

  const getContacts = () => {
    if (currentUserType === 'seller') {
        // En selger kan chatte med partnere (som ledere) eller admins
        return safePartners.map(p => ({ id: p.id, name: p.name, role: 'Partner / Leder', avatar: 'FiBriefcase' }));
    }
    if (currentUserType === 'partner') {
        // En partner kan chatte med sine selgere
        return safeSellers.map(s => ({ id: s.id, name: s.name, role: 'Selger', avatar: 'FiUser' }));
    }
    if (currentUserType === 'superadmin') {
        // Superadmin kan chatte med alle partnere
        return safePartners.map(p => ({ id: p.id, name: p.name, role: 'Partner', avatar: 'FiBriefcase' }));
    }
    return [];
  };

  const contacts = getContacts();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [internalMessages, activeConversation]);

  useEffect(() => {
    if (activeConversation && typeof markMessagesAsRead === 'function' && user) {
        markMessagesAsRead(user.id, activeConversation.id);
    }
  }, [activeConversation, internalMessages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !activeConversation) return;
    
    sendInternalMessage(user.id, activeConversation.id, messageInput, user.name);
    setMessageInput('');
  };

  const getUnreadForContact = (contactId) => {
    if (!Array.isArray(internalMessages)) return 0;
    return internalMessages.filter(
        msg => msg.sender_id === contactId && msg.receiver_id === user.id && !msg.read
    ).length;
  };

  // Hent meldinger sikkert
  const currentMessages = activeConversation ? getChatHistory(user.id, activeConversation.id) : [];

  return (
    <div className="flex h-[600px] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Sidebar: Contacts */}
      <div className="w-1/3 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-gray-100">
          <h3 className="font-bold text-gray-700 flex items-center gap-2">
            <SafeIcon icon={currentUserType === 'superadmin' ? FiShield : FiUser} />
            {currentUserType === 'seller' ? 'Din Leder' : currentUserType === 'superadmin' ? 'Partnere' : 'Dine Selgere'}
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {contacts.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Ingen kontakter funnet.
            </div>
          ) : (
            contacts.map(contact => {
              const unread = getUnreadForContact(contact.id);
              return (
                <button
                  key={contact.id}
                  onClick={() => setActiveConversation(contact)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-white transition-colors border-b border-gray-100 ${activeConversation?.id === contact.id ? 'bg-white border-l-4 border-l-blue-500' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${contact.avatar === 'FiBriefcase' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                    <SafeIcon icon={contact.avatar === 'FiBriefcase' ? FiBriefcase : FiUser} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 text-sm">{contact.name}</p>
                    <p className="text-xs text-gray-500">{contact.role}</p>
                  </div>
                  {unread > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">{unread}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white">
        {activeConversation ? (
          <>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                  <SafeIcon icon={FiMessageSquare} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{activeConversation.name}</h3>
                  <p className="text-xs text-green-600 flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Tilgjengelig</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {currentMessages.length === 0 ? (
                <div className="text-center text-gray-400 mt-10"><p>Start samtalen med {activeConversation.name}</p></div>
              ) : (
                currentMessages.map((msg, index) => {
                  const isMe = msg.sender_id === user.id;
                  return (
                    <div key={index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl p-3 shadow-sm ${isMe ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'}`}>
                        <p className="text-sm">{msg.content}</p>
                        <div className={`text-[10px] mt-1 flex items-center justify-end gap-1 ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          {isMe && <SafeIcon icon={msg.read ? FiCheckCircle : FiCheck} />}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Skriv en melding..."
                  className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button type="submit" disabled={!messageInput.trim()} className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <SafeIcon icon={FiSend} className="text-lg" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <SafeIcon icon={FiMessageSquare} className="text-6xl mb-4 text-gray-200" />
            <p>Velg en kontakt for å starte chatten</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalChat;